import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from '@/i18n';
import { useAuth } from '@/features/auth/context/useAuth';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { smoothScrollTo } from './scrollUtils';

export function LandingNav() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on Escape key
  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen]);

  const scrollToSection = useCallback((id: string) => {
    setMobileOpen(false);
    if (location.pathname !== '/') {
      void navigate(`/#${id}`);
      return;
    }
    smoothScrollTo(id);
  }, [location.pathname, navigate]);

  const goToLogin = () => {
    setMobileOpen(false);
    void navigate('/merchant/login');
  };

  const goToDashboard = () => {
    setMobileOpen(false);
    void navigate('/merchant/dashboard');
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/90 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.svg" alt={t('common.brand.name')} className="h-8 w-8" />
            <span className="text-xl font-bold text-gray-900">
              {t('common.brand.name')}
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <button
              onClick={() => scrollToSection('how-it-works')}
              className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
            >
              {t('landing.nav.howItWorks')}
            </button>
            <button
              onClick={() => scrollToSection('features')}
              className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
            >
              {t('landing.nav.features')}
            </button>
            <LanguageSwitcher />
            {isAuthenticated ? (
              <button
                onClick={goToDashboard}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-lg bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-600 hover:to-indigo-600 transition-all cursor-pointer"
              >
                {t('landing.nav.dashboard')}
              </button>
            ) : (
              <>
                <button
                  onClick={goToLogin}
                  className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
                >
                  {t('landing.nav.signIn')}
                </button>
                <button
                  onClick={goToLogin}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-lg bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-600 hover:to-indigo-600 transition-all cursor-pointer"
                >
                  {t('landing.nav.getStarted')}
                </button>
              </>
            )}
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden p-2 text-gray-600 hover:text-gray-900 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={t('landing.nav.toggleMenu')}
          >
            {mobileOpen ? (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-4 py-4 space-y-3">
            <button
              onClick={() => scrollToSection('how-it-works')}
              className="block w-full text-left text-sm font-medium text-gray-600 hover:text-gray-900 py-2 cursor-pointer"
            >
              {t('landing.nav.howItWorks')}
            </button>
            <button
              onClick={() => scrollToSection('features')}
              className="block w-full text-left text-sm font-medium text-gray-600 hover:text-gray-900 py-2 cursor-pointer"
            >
              {t('landing.nav.features')}
            </button>
            <div className="py-2">
              <LanguageSwitcher />
            </div>
            <hr className="border-gray-100" />
            {isAuthenticated ? (
              <button
                onClick={goToDashboard}
                className="block w-full text-center px-4 py-3 text-sm font-medium text-white rounded-lg bg-gradient-to-r from-sky-500 to-indigo-500 cursor-pointer"
              >
                {t('landing.nav.dashboard')}
              </button>
            ) : (
              <>
                <button
                  onClick={goToLogin}
                  className="block w-full text-center text-sm font-medium text-gray-600 hover:text-gray-900 py-2 cursor-pointer"
                >
                  {t('landing.nav.signIn')}
                </button>
                <button
                  onClick={goToLogin}
                  className="block w-full text-center px-4 py-3 text-sm font-medium text-white rounded-lg bg-gradient-to-r from-sky-500 to-indigo-500 cursor-pointer"
                >
                  {t('landing.nav.getStarted')}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
